import React, { useState, useEffect, useLayoutEffect } from "react";
import { useDispatch } from "react-redux";
import { setSelLocation, setLocations } from "../store/slices/settingSlice";
import { useGetCartItems } from "hooks";
import { useGetAccessToken, useGetLocations } from "hooks/api";
import httpService from "core/api/httpService";
import { isDateBeforeToday, loadCategoryTypeIdsFromSetting, setFilterDates } from "utils";

export interface AuthContextType {
  token: string;
  selCategoryTypeIds: number[];
  setSelCategoryTypeIds: Function;
  availFlag: boolean;
  setAvailFlag: Function;
}

const AuthContext = React.createContext({} as AuthContextType);

export default AuthContext;

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const dispatch = useDispatch();
  const [selCategoryTypeIds, setSelCategoryTypeIds] = useState<number[]>([]);
  const [availFlag, setAvailFlag] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const { data: accessTokenData } = useGetAccessToken();
  const accessToken = accessTokenData?.data?.accessToken;

  const { data: locationsData } = useGetLocations({ accessToken });
  const locations = locationsData?.data?.locations;

  React.useEffect(() => {
    if (!accessToken) return;

    localStorage.setItem('TOKEN', accessToken);
    httpService.setAccessToken();
  }, [accessToken]);

  React.useEffect(() => {
    if (!locations) return;

    const locOptions = locations.length > 1
      ? [{ label: "All Locations", value: "" }] : [];

    locations.forEach((loc: any) => {
      let label = loc?.name;
      let item = { label: label, value: loc?.id,obj: loc };
      locOptions.push(item);
    });

    const location = JSON.parse(window.localStorage.getItem("Setting") || '{}')?.location || null;

    dispatch(setLocations(locOptions));

    if (locOptions.length <= 1 || !location) {
      dispatch(setSelLocation(locOptions?.[0]));
      return;
    }
    dispatch(setSelLocation(location));
  }, [locations]);

  useGetCartItems();

  useEffect(() => {
    if (!accessToken) return;

    const settingInfo = JSON.parse(window.localStorage.getItem("Setting") || '{}');
    window.localStorage.setItem(
      "Setting",
      JSON.stringify({
        ...settingInfo,
        categoryTypeIds: selCategoryTypeIds || [],
        availFlag
      })
    );
  }, [selCategoryTypeIds, availFlag, accessToken]);

  useLayoutEffect(() => {
    const checkMobileMode = () => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      setIsMobile(isMobile);
    };
    const settingInfo = JSON.parse(window.localStorage.getItem("Setting") || '{}');
    const localFilterDate = settingInfo.dateFilter || {};

    if (localFilterDate?.pickupDate && isDateBeforeToday(localFilterDate?.pickupDate)) {
      localFilterDate['pickupDate'] = undefined;
      localFilterDate['pickupTime'] = undefined;
    }
    if (localFilterDate?.returnDate && isDateBeforeToday(localFilterDate?.returnDate)) {
      localFilterDate['returnDate'] = undefined;
      localFilterDate['returnTime'] = undefined;
    }
    setSelCategoryTypeIds(loadCategoryTypeIdsFromSetting(settingInfo));
    setAvailFlag(settingInfo?.availFlag || true);
    setFilterDates(localFilterDate);
    dispatch(setSelLocation(settingInfo?.location));

    checkMobileMode();
    window.addEventListener('resize', checkMobileMode);

    return () => {
      window.removeEventListener('resize', checkMobileMode);
    };
  }, []);

  const contextData = {
    token: accessToken,
    isMobile,
    selCategoryTypeIds,
    setSelCategoryTypeIds,
    availFlag,
    setAvailFlag,
  } as AuthContextType;

  return (
    <AuthContext.Provider value={contextData}>
      {children}
    </AuthContext.Provider>
  );
};
